/**
 * 工作流监控系统
 * 提供实时监控、日志记录和性能分析
 */

import { EventEmitter } from 'events';
import { WorkflowExecutionResult, WorkflowExecutionContext } from '../types/workflow-v2';
import fs from 'fs-extra';
import path from 'path';
import { logger } from '../utils/logger';
import { colors } from '../utils/colors';

export interface WorkflowMetrics {
  executionId: string;
  workflowId: string;
  startTime: Date;
  endTime?: Date;
  duration?: number;
  status: 'running' | 'success' | 'failure' | 'cancelled';
  nodeMetrics: Map<string, NodeMetrics>;
  resourceUsage?: ResourceUsage;
  errors: ErrorMetrics[];
}

export interface NodeMetrics {
  nodeId: string;
  nodeName: string;
  nodeType: string;
  startTime: Date;
  endTime?: Date;
  duration?: number;
  status: 'pending' | 'running' | 'success' | 'failure' | 'skipped';
  retryCount?: number;
  iterations?: number;
  result?: any;
  error?: string;
}

export interface ResourceUsage {
  cpuUsage: number;
  memoryUsage: number;
  diskIO?: number;
  networkIO?: number;
}

export interface ErrorMetrics {
  nodeId: string;
  timestamp: Date;
  error: string;
  stack?: string;
  severity: 'warning' | 'error' | 'critical';
}

export interface MonitorOptions {
  logDir?: string;
  metricsInterval?: number;
  enableResourceMonitoring?: boolean;
  enableTracing?: boolean;
  webhookUrl?: string;
}

export class WorkflowMonitor extends EventEmitter {
  private metrics: Map<string, WorkflowMetrics> = new Map();
  private logDir: string;
  private options: MonitorOptions;
  private metricsTimer?: NodeJS.Timer;
  
  constructor(options: MonitorOptions = {}) {
    super();
    this.options = {
      logDir: path.join(process.cwd(), '.workflow', 'logs'),
      metricsInterval: 5000,
      enableResourceMonitoring: true,
      enableTracing: false,
      ...options
    };
    
    this.logDir = this.options.logDir!;
    this.initialize();
  }
  
  private async initialize() {
    await fs.ensureDir(this.logDir);
    await fs.ensureDir(path.join(this.logDir, 'executions'));
    await fs.ensureDir(path.join(this.logDir, 'metrics'));
    await fs.ensureDir(path.join(this.logDir, 'traces'));
    
    if (this.options.enableResourceMonitoring) {
      this.startResourceMonitoring();
    }
  }
  
  /**
   * 开始监控工作流执行
   */
  async startMonitoring(executionId: string, workflowId: string): Promise<void> {
    const metrics: WorkflowMetrics = {
      executionId,
      workflowId,
      startTime: new Date(),
      status: 'running',
      nodeMetrics: new Map(),
      errors: []
    };
    
    this.metrics.set(executionId, metrics);
    
    // 记录开始日志
    await this.logEvent(executionId, 'workflow:start', {
      workflowId,
      timestamp: metrics.startTime
    });
    
    // 发送 webhook 通知
    if (this.options.webhookUrl) {
      this.sendWebhook('workflow:start', metrics);
    }
    
    this.emit('monitoring:started', { executionId, workflowId });
  }
  
  /**
   * 记录节点开始
   */
  async recordNodeStart(executionId: string, nodeId: string, nodeInfo: any): Promise<void> {
    const metrics = this.metrics.get(executionId);
    if (!metrics) return;
    
    const nodeMetrics: NodeMetrics = {
      nodeId,
      nodeName: nodeInfo.name,
      nodeType: nodeInfo.type,
      startTime: new Date(),
      status: 'running'
    };
    
    metrics.nodeMetrics.set(nodeId, nodeMetrics);
    
    await this.logEvent(executionId, 'node:start', {
      nodeId,
      nodeName: nodeInfo.name,
      nodeType: nodeInfo.type,
      timestamp: nodeMetrics.startTime
    });
  }
  
  /**
   * 记录节点完成
   */
  async recordNodeComplete(executionId: string, nodeId: string, result?: any): Promise<void> {
    const metrics = this.metrics.get(executionId);
    if (!metrics) return;
    
    const nodeMetrics = metrics.nodeMetrics.get(nodeId);
    if (!nodeMetrics) return;
    
    nodeMetrics.endTime = new Date();
    nodeMetrics.duration = nodeMetrics.endTime.getTime() - nodeMetrics.startTime.getTime();
    nodeMetrics.status = 'success';
    nodeMetrics.result = result;
    
    await this.logEvent(executionId, 'node:complete', {
      nodeId,
      duration: nodeMetrics.duration,
      timestamp: nodeMetrics.endTime
    });
  }
  
  /**
   * 记录节点错误
   */
  async recordNodeError(executionId: string, nodeId: string, error: Error): Promise<void> {
    const metrics = this.metrics.get(executionId);
    if (!metrics) return;
    
    const nodeMetrics = metrics.nodeMetrics.get(nodeId);
    if (nodeMetrics) {
      nodeMetrics.endTime = new Date();
      nodeMetrics.duration = nodeMetrics.endTime.getTime() - nodeMetrics.startTime.getTime();
      nodeMetrics.status = 'failure';
      nodeMetrics.error = error.message;
    }
    
    const errorMetric: ErrorMetrics = {
      nodeId,
      timestamp: new Date(),
      error: error.message,
      stack: error.stack,
      severity: this.classifyErrorSeverity(error)
    };
    
    metrics.errors.push(errorMetric);
    
    await this.logEvent(executionId, 'node:error', {
      nodeId,
      error: error.message,
      severity: errorMetric.severity,
      timestamp: errorMetric.timestamp
    });
    
    // 发送关键错误通知
    if (errorMetric.severity === 'critical' && this.options.webhookUrl) {
      this.sendWebhook('critical:error', {
        executionId,
        nodeId,
        error: errorMetric
      });
    }
  }
  
  /**
   * 完成监控
   */
  async completeMonitoring(executionId: string, result: WorkflowExecutionResult): Promise<void> {
    const metrics = this.metrics.get(executionId);
    if (!metrics) return;
    
    metrics.endTime = new Date();
    metrics.duration = metrics.endTime.getTime() - metrics.startTime.getTime();
    metrics.status = result.status === 'success' ? 'success' : 'failure';
    
    // 生成执行报告
    await this.generateExecutionReport(executionId, metrics);
    
    // 保存指标
    await this.saveMetrics(executionId, metrics);
    
    // 记录完成日志
    await this.logEvent(executionId, 'workflow:complete', {
      status: metrics.status,
      duration: metrics.duration,
      timestamp: metrics.endTime,
      nodeCount: metrics.nodeMetrics.size,
      errorCount: metrics.errors.length
    });
    
    // 发送完成通知
    if (this.options.webhookUrl) {
      this.sendWebhook('workflow:complete', metrics);
    }
    
    this.emit('monitoring:completed', { executionId, metrics });
    
    // 清理内存中的指标
    setTimeout(() => {
      this.metrics.delete(executionId);
    }, 60000); // 1分钟后清理
  }
  
  /**
   * 生成执行报告
   */
  private async generateExecutionReport(executionId: string, metrics: WorkflowMetrics): Promise<void> {
    const reportPath = path.join(this.logDir, 'executions', `${executionId}-report.json`);
    
    const report = {
      summary: {
        executionId,
        workflowId: metrics.workflowId,
        status: metrics.status,
        startTime: metrics.startTime,
        endTime: metrics.endTime,
        duration: metrics.duration,
        totalNodes: metrics.nodeMetrics.size,
        successfulNodes: Array.from(metrics.nodeMetrics.values()).filter(n => n.status === 'success').length,
        failedNodes: Array.from(metrics.nodeMetrics.values()).filter(n => n.status === 'failure').length,
        skippedNodes: Array.from(metrics.nodeMetrics.values()).filter(n => n.status === 'skipped').length,
        errorCount: metrics.errors.length
      },
      nodes: Array.from(metrics.nodeMetrics.values()).map(node => ({
        id: node.nodeId,
        name: node.nodeName,
        type: node.nodeType,
        status: node.status,
        startTime: node.startTime,
        endTime: node.endTime,
        duration: node.duration,
        retryCount: node.retryCount,
        iterations: node.iterations,
        error: node.error
      })),
      errors: metrics.errors,
      resourceUsage: metrics.resourceUsage,
      timeline: await this.generateTimeline(executionId)
    };
    
    await fs.writeJSON(reportPath, report, { spaces: 2 });
    logger.info(`Execution report saved: ${reportPath}`);
  }
  
  /**
   * 生成执行时间线
   */
  private async generateTimeline(executionId: string): Promise<any[]> {
    const logPath = path.join(this.logDir, 'executions', `${executionId}.log`);
    if (!await fs.pathExists(logPath)) return [];
    
    const logs = await fs.readFile(logPath, 'utf-8');
    const events = logs.split('\n')
      .filter(line => line.trim())
      .map(line => {
        try {
          return JSON.parse(line);
        } catch {
          return null;
        }
      })
      .filter(event => event !== null);
    
    return events;
  }
  
  /**
   * 保存指标
   */
  private async saveMetrics(executionId: string, metrics: WorkflowMetrics): Promise<void> {
    const date = new Date().toISOString().split('T')[0];
    const metricsPath = path.join(this.logDir, 'metrics', `${date}-metrics.jsonl`);
    
    const metricsData = {
      executionId,
      workflowId: metrics.workflowId,
      timestamp: new Date(),
      duration: metrics.duration,
      status: metrics.status,
      nodeCount: metrics.nodeMetrics.size,
      errorCount: metrics.errors.length,
      resourceUsage: metrics.resourceUsage
    };
    
    await fs.appendFile(metricsPath, JSON.stringify(metricsData) + '\n');
  }
  
  /**
   * 记录事件日志
   */
  private async logEvent(executionId: string, event: string, data: any): Promise<void> {
    const logPath = path.join(this.logDir, 'executions', `${executionId}.log`);
    const logEntry = {
      timestamp: new Date(),
      event,
      executionId,
      ...data
    };
    
    await fs.appendFile(logPath, JSON.stringify(logEntry) + '\n');
    
    if (this.options.enableTracing) {
      await this.saveTrace(executionId, event, logEntry);
    }
  }
  
  /**
   * 保存追踪信息
   */
  private async saveTrace(executionId: string, event: string, data: any): Promise<void> {
    const tracePath = path.join(this.logDir, 'traces', `${executionId}-trace.jsonl`);
    const traceEntry = {
      timestamp: new Date(),
      event,
      data,
      stack: new Error().stack
    };
    
    await fs.appendFile(tracePath, JSON.stringify(traceEntry) + '\n');
  }
  
  /**
   * 开始资源监控
   */
  private startResourceMonitoring(): void {
    this.metricsTimer = setInterval(() => {
      for (const [executionId, metrics] of this.metrics) {
        if (metrics.status === 'running') {
          metrics.resourceUsage = this.collectResourceUsage();
        }
      }
    }, this.options.metricsInterval);
  }
  
  /**
   * 收集资源使用情况
   */
  private collectResourceUsage(): ResourceUsage {
    const usage = process.cpuUsage();
    const memUsage = process.memoryUsage();
    
    return {
      cpuUsage: (usage.user + usage.system) / 1000000, // 转换为秒
      memoryUsage: memUsage.heapUsed / 1024 / 1024, // 转换为 MB
      diskIO: 0, // TODO: 实现磁盘 IO 监控
      networkIO: 0 // TODO: 实现网络 IO 监控
    };
  }
  
  /**
   * 分类错误严重性
   */
  private classifyErrorSeverity(error: Error): 'warning' | 'error' | 'critical' {
    const message = error.message.toLowerCase();
    
    if (message.includes('critical') || 
        message.includes('fatal') || 
        message.includes('rollback failed')) {
      return 'critical';
    }
    
    if (message.includes('timeout') || 
        message.includes('retry') || 
        message.includes('warning')) {
      return 'warning';
    }
    
    return 'error';
  }
  
  /**
   * 发送 Webhook 通知
   */
  private async sendWebhook(event: string, data: any): Promise<void> {
    if (!this.options.webhookUrl) return;
    
    try {
      await fetch(this.options.webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          event,
          timestamp: new Date(),
          data
        })
      });
    } catch (error) {
      logger.error('Failed to send webhook:', error);
    }
  }
  
  /**
   * 获取实时指标
   */
  getMetrics(executionId: string): WorkflowMetrics | undefined {
    return this.metrics.get(executionId);
  }
  
  /**
   * 获取所有运行中的工作流
   */
  getRunningWorkflows(): WorkflowMetrics[] {
    return Array.from(this.metrics.values()).filter(m => m.status === 'running');
  }
  
  /**
   * 生成性能报告
   */
  async generatePerformanceReport(startDate: Date, endDate: Date): Promise<any> {
    const metricsDir = path.join(this.logDir, 'metrics');
    const files = await fs.readdir(metricsDir);
    
    const metrics: any[] = [];
    
    for (const file of files) {
      if (file.endsWith('-metrics.jsonl')) {
        const content = await fs.readFile(path.join(metricsDir, file), 'utf-8');
        const lines = content.split('\n').filter(line => line.trim());
        
        for (const line of lines) {
          try {
            const metric = JSON.parse(line);
            const timestamp = new Date(metric.timestamp);
            
            if (timestamp >= startDate && timestamp <= endDate) {
              metrics.push(metric);
            }
          } catch (error) {
            logger.error(`Failed to parse metric: ${error}`);
          }
        }
      }
    }
    
    // 分析指标
    const report = {
      period: {
        start: startDate,
        end: endDate
      },
      summary: {
        totalExecutions: metrics.length,
        successfulExecutions: metrics.filter(m => m.status === 'success').length,
        failedExecutions: metrics.filter(m => m.status === 'failure').length,
        averageDuration: metrics.reduce((sum, m) => sum + (m.duration || 0), 0) / metrics.length,
        totalErrors: metrics.reduce((sum, m) => sum + (m.errorCount || 0), 0)
      },
      byWorkflow: this.groupMetricsByWorkflow(metrics),
      byStatus: this.groupMetricsByStatus(metrics),
      performance: {
        p50Duration: this.calculatePercentile(metrics.map(m => m.duration || 0), 50),
        p95Duration: this.calculatePercentile(metrics.map(m => m.duration || 0), 95),
        p99Duration: this.calculatePercentile(metrics.map(m => m.duration || 0), 99)
      }
    };
    
    return report;
  }
  
  /**
   * 按工作流分组指标
   */
  private groupMetricsByWorkflow(metrics: any[]): any {
    const grouped: Record<string, any> = {};
    
    for (const metric of metrics) {
      if (!grouped[metric.workflowId]) {
        grouped[metric.workflowId] = {
          count: 0,
          successful: 0,
          failed: 0,
          totalDuration: 0,
          totalErrors: 0
        };
      }
      
      const group = grouped[metric.workflowId];
      group.count++;
      if (metric.status === 'success') group.successful++;
      if (metric.status === 'failure') group.failed++;
      group.totalDuration += metric.duration || 0;
      group.totalErrors += metric.errorCount || 0;
    }
    
    // 计算平均值
    for (const workflowId in grouped) {
      const group = grouped[workflowId];
      group.averageDuration = group.totalDuration / group.count;
      group.successRate = group.successful / group.count;
      group.averageErrors = group.totalErrors / group.count;
    }
    
    return grouped;
  }
  
  /**
   * 按状态分组指标
   */
  private groupMetricsByStatus(metrics: any[]): any {
    return {
      success: metrics.filter(m => m.status === 'success').length,
      failure: metrics.filter(m => m.status === 'failure').length,
      cancelled: metrics.filter(m => m.status === 'cancelled').length
    };
  }
  
  /**
   * 计算百分位数
   */
  private calculatePercentile(values: number[], percentile: number): number {
    if (values.length === 0) return 0;
    
    const sorted = values.sort((a, b) => a - b);
    const index = Math.ceil((percentile / 100) * sorted.length) - 1;
    
    return sorted[index];
  }
  
  /**
   * 停止监控
   */
  stop(): void {
    if (this.metricsTimer) {
      clearInterval(this.metricsTimer);
    }
  }
}